import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, Validators } from '@angular/forms';
import { finalize } from 'rxjs/operators';

import { injectModalRef } from '../../../../shared/modal';
import { SettingsService, TwoFactorSetup } from '../../../../core/services';
import { apiErrorMessage } from '../../../../core/utils/api-error-message';

@Component({
  selector: 'app-two-factor-setup-modal',
  standalone: false,
  templateUrl: './two-factor-setup-modal.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TwoFactorSetupModal implements OnInit {
  readonly modalRef = injectModalRef<string[]>();

  private readonly settingsService = inject(SettingsService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly setup = signal<TwoFactorSetup | null>(null);
  protected readonly loadError = signal<string | null>(null);
  protected readonly verifying = signal(false);
  protected readonly verifyError = signal<string | null>(null);
  protected readonly showSecret = signal(false);

  protected readonly code = new FormControl('', {
    nonNullable: true,
    validators: [Validators.required, Validators.pattern(/^\d{6}$/)],
  });

  ngOnInit(): void {
    this.loadSetup();
  }

  protected loadSetup(): void {
    this.loadError.set(null);
    this.setup.set(null);
    this.settingsService.setupTwoFactor()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: setup => this.setup.set(setup),
        error: error => this.loadError.set(apiErrorMessage(error, 'Could not start two-factor authentication setup. Please try again.')),
      });
  }

  protected verify(): void {
    if (this.verifying()) return;
    if (this.code.invalid) {
      this.code.markAsTouched();
      return;
    }

    this.verifying.set(true);
    this.verifyError.set(null);
    this.modalRef.setDisableClose(true);

    this.settingsService.confirmTwoFactor(this.code.value.trim())
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => {
          this.verifying.set(false);
          this.modalRef.setDisableClose(false);
        }),
      )
      .subscribe({
        next: result => this.modalRef.close(result.backupCodes),
        error: error => {
          this.verifyError.set(apiErrorMessage(error, 'That code did not work. Please try again.'));
          this.code.reset();
        },
      });
  }
}
